import React from 'react'
import { useAuth } from '../../contexts/AuthContextLazy'
import { MFASetup } from '../../components/auth/MFASetup'
import { UserCircleIcon, ShieldCheckIcon, BellIcon, CreditCardIcon } from '@heroicons/react/24/outline'
import { isSettingsSectionVisible } from '../../config/portalFeatureFlags'
import '../../styles/portal-ui-polish.css'

export const Settings: React.FC = () => {
  const { userProfile, organization } = useAuth()

  return (
    <div className="portal-page-bg min-h-screen">
      <div className="py-4 sm:py-6 lg:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4 sm:space-y-6">
            {/* Profile Section */}
            {isSettingsSectionVisible('profile') && (
              <div className="portal-card">
                <div className="px-4 py-4 sm:p-6">
                  <div className="flex items-center mb-4 sm:mb-6">
                    <UserCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mr-2 sm:mr-3" />
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Profile Information</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-500">Full Name</label>
                      <p className="text-sm sm:text-base text-gray-900 mt-1">{userProfile?.full_name || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-500">Email</label>
                      <p className="text-sm sm:text-base text-gray-900 mt-1 truncate">{userProfile?.id || 'Not available'}</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-500">Role</label>
                      <p className="text-sm sm:text-base text-gray-900 capitalize mt-1">{userProfile?.role.replace('_', ' ') || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-gray-500">Organization</label>
                      <p className="text-sm sm:text-base text-gray-900 mt-1">{organization?.name || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Section */}
            {isSettingsSectionVisible('security') && (
              <div className="portal-card">
                <div className="px-4 py-4 sm:p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mr-2 sm:mr-3" />
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Security</h2>
                  </div>
                </div>
                <div className="px-4 py-4 sm:p-6">
                  <MFASetup />
                </div>
              </div>
            )}

            {/* Notification Preferences */}
            {isSettingsSectionVisible('notifications') && (
              <div className="portal-card">
                <div className="px-4 py-4 sm:p-6">
                  <div className="flex items-center mb-4 sm:mb-6">
                    <BellIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mr-2 sm:mr-3" />
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Notification Preferences</h2>
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    <label className="flex items-start sm:items-center cursor-pointer">
                      <input type="checkbox" className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 mt-0.5 sm:mt-0" defaultChecked />
                      <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">Email notifications for new documents</span>
                    </label>
                    <label className="flex items-start sm:items-center cursor-pointer">
                      <input type="checkbox" className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 mt-0.5 sm:mt-0" defaultChecked />
                      <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">Email notifications for account updates</span>
                    </label>
                    <label className="flex items-start sm:items-center cursor-pointer">
                      <input type="checkbox" className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 mt-0.5 sm:mt-0" />
                      <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">Marketing communications</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Billing Information (if applicable) */}
            {isSettingsSectionVisible('billing') && organization?.subscription_tier && (
              <div className="portal-card">
                <div className="px-4 py-4 sm:p-6">
                  <div className="flex items-center mb-4 sm:mb-6">
                    <CreditCardIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mr-2 sm:mr-3" />
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Subscription</h2>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-500">Current Plan</label>
                    <p className="text-sm sm:text-base text-gray-900 capitalize mt-1">{organization.subscription_tier}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}